import React from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import KibDivider from '@chewy/kib-divider-react'
import { KibCloseIcon } from '@chewy/kib-icons-react'

import '@chewy/kib-divider-styles/dist/kib-divider.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './OverlayPanel.module.scss'

interface OverlayPanelProps {
  bodyContent: React.ReactNode
  destructiveButton?: {
    disabled?: boolean
    label: string
    onClick: () => void
  }
  handleClose: () => void
  hideFooter?: boolean
  isLoading?: boolean
  primaryButton?: {
    disabled?: boolean
    label: string
    onClick: () => void
  }
  secondaryButton?: {
    label: string
    onClick: () => void
  }
  subtitle?: string
  title: string
}

const OverlayPanel = ({
  subtitle,
  title,
  destructiveButton,
  primaryButton,
  secondaryButton,
  handleClose,
  bodyContent,
  hideFooter,
  isLoading,
}: OverlayPanelProps) => {
  const { t } = useTranslation(['Common'])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headingTextContainer}>
          {isLoading && <div className={styles.loadingTitle} />}
          {!isLoading && (
            <>
              <h1 className={styles.title}>{title}</h1>
              {subtitle && <span>{subtitle}</span>}
            </>
          )}
        </div>
        <div>
          <KibButtonNew
            aria-label={t('Common:CLOSE_ACTION')}
            emphasis="tertiary"
            icon={<KibCloseIcon />}
            theme="action"
            onClick={handleClose}
          />
        </div>
      </div>
      <KibDivider className={styles.divider} />
      <div className={styles.bodyContentContainer}>{bodyContent}</div>
      {!hideFooter && (
        <>
          <KibDivider className={styles.divider} />
          <div className={styles.footer} data-testid="overlay-panel-footer">
            <KibButtonNew
              className={styles.closeButton}
              size="small"
              onClick={handleClose}
            >
              {t('Common:CLOSE_ACTION')}
            </KibButtonNew>
            <div className={styles.ctaButtonsContainer}>
              {destructiveButton && (
                <KibButtonNew
                  disabled={destructiveButton.disabled}
                  theme="destructive"
                  onClick={destructiveButton.onClick}
                >
                  {destructiveButton.label}
                </KibButtonNew>
              )}
              {secondaryButton && (
                <KibButtonNew
                  emphasis="secondary"
                  onClick={secondaryButton.onClick}
                >
                  {secondaryButton.label}
                </KibButtonNew>
              )}
              {primaryButton && (
                <KibButtonNew
                  disabled={primaryButton.disabled}
                  onClick={primaryButton.onClick}
                >
                  {primaryButton.label}
                </KibButtonNew>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default OverlayPanel
